import { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getActiveSlides, getActiveSlidesForTotalsOnly } from '../config/slides'
import type { WrappedTotalsData, WrappedData } from '../types/wrapped'
import {
  wrappedStoryStateReducer,
  WrappedStoryActions,
  INITIAL_WRAPPED_STORY_STATE,
  WrappedStoryState,
  WrappedStoryDispatchAction,
} from 'contexts/Wrapped'
import { getMyWrapped, getSharedWrapped, getWrappedTotals } from 'api/wrapped/wrapped'
import { getWindowMockData } from '../devMocks'
import { useAppSelector } from 'hooks/reduxHooks'
import useWrappedPredictionData from './useWrappedPredictionData'

interface UseWrappedDataOptions {
  uuid?: string
}

interface UseWrappedDataResult {
  /** True while API data is being fetched */
  isLoading: boolean
  /** True if there was a non-404 error fetching data */
  hasError: boolean
  error: Error | null
  data: WrappedData | undefined
  totalsData: WrappedTotalsData | undefined
  state: WrappedStoryState
  dispatch: React.Dispatch<WrappedStoryDispatchAction>
  handleRetry: () => void
  /** True when we have slides ready to display */
  hasContent: boolean
  /** True while loading overlay should be shown (data loading OR first slide video buffering) */
  showLoadingOverlay: boolean
}

function useWrappedData({ uuid }: UseWrappedDataOptions): UseWrappedDataResult {
  const [state, dispatch] = useReducer(
    wrappedStoryStateReducer,
    INITIAL_WRAPPED_STORY_STATE,
  )

  const regionCode = useAppSelector(state => state.geo.currentRegionCode)

  // Fetch totals data (always needed for fallbacks)
  const {
    data: totalsData,
    isLoading: isTotalsDataLoading,
    isError: totalsDataError,
  } = useQuery({
    queryKey: ['wrapped-totals', regionCode],
    queryFn: () => getWrappedTotals(regionCode),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch user-specific wrapped data
  const {
    data,
    isLoading: isUserDataLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['wrapped', uuid ?? 'me'],
    queryFn: async() => {
      // Check for mock data first (development/QA only)
      const mockData = getWindowMockData()
      if (mockData) return mockData

      return uuid ? getSharedWrapped(uuid) : getMyWrapped()
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 404 errors - user has no wrapped data
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return false
      }
      return failureCount < 3
    },
  })

  // Check if the error is a 404 (user has no wrapped data)
  const is404Error = isError && error && typeof error === 'object' && 'status' in error && error.status === 404

  // Set totals data in context when available
  useEffect(() => {
    if (totalsData) {
      dispatch({
        type: WrappedStoryActions.setTotalsData,
        totalsData,
      })
    }
  }, [totalsData])

  // Set user data in context when available
  useEffect(() => {
    if (data) {
      const slides = getActiveSlides(data)
      dispatch({
        type: WrappedStoryActions.setData,
        data,
        slides,
      })
    }
  }, [data])

  // Handle 404 case: set up slides for totals-only mode (no wrapped data)
  useEffect(() => {
    if (is404Error && totalsData) {
      const slides = getActiveSlidesForTotalsOnly(totalsData)
      dispatch({
        type: WrappedStoryActions.setTotalsSlides,
        slides,
        totalsData,
      })
    }
  }, [is404Error, totalsData])

  // Pre-fetch prediction slide data
  const { isLoading: isPredictionDataLoading } = useWrappedPredictionData({
    predictionDestinationName: data?.predictionDestinationName,
    activeBooking: data?.activeBooking,
    dispatch,
  })

  const handleRetry = useCallback(() => {
    refetch()
  }, [refetch])

  // Still loading if user data is loading and we don't have totals to fall back to
  const isLoading = (isUserDataLoading && !is404Error) || isTotalsDataLoading

  // Ready to show content when we have slides (either from user data or totals-only mode)
  const hasContent = state.slides.length > 0

  // Track if user has ever left the first slide (don't show loading overlay if they come back)
  const hasLeftFirstSlideRef = useRef(false)
  if (state.currentIndex > 0) {
    hasLeftFirstSlideRef.current = true
  }

  // Check if first slide has a video that needs to buffer
  const firstSlideHasVideo = state.slides[0]?.video !== undefined
  const isOnFirstSlide = state.currentIndex === 0

  // Fallback timeout to prevent loading overlay from getting stuck indefinitely
  // This can happen on mobile when video autoplay is blocked by the browser
  const [videoReadyTimeout, setVideoReadyTimeout] = useState(false)
  useEffect(() => {
    // Only set timeout if we have content and are on first slide with a video
    if (!hasContent || !isOnFirstSlide || !firstSlideHasVideo || state.isVideoReady) {
      return
    }

    const timeoutId = setTimeout(() => {
      setVideoReadyTimeout(true)
    }, 5000) // Allow 5 seconds for video to start before assuming it's ready/blocked

    return () => clearTimeout(timeoutId)
  }, [hasContent, isOnFirstSlide, firstSlideHasVideo, state.isVideoReady])

  // Show loading overlay while data is loading OR while first slide video is buffering
  // Also wait for prediction data if needed (to prevent flickering on prediction slide)
  // Only show on first slide and only if user hasn't navigated away yet
  // Use videoReadyTimeout as a fallback to prevent infinite loading on mobile (autoplay blocked)
  const showLoadingOverlay = isLoading || isPredictionDataLoading || (
    hasContent &&
    isOnFirstSlide &&
    !hasLeftFirstSlideRef.current &&
    firstSlideHasVideo &&
    !state.isVideoReady &&
    !videoReadyTimeout
  )

  return {
    isLoading,
    hasError: (isError && !is404Error) || totalsDataError, // 404 is not an error - we use totals-only mode
    error: is404Error ? null : (error as Error | null),
    data,
    totalsData,
    state,
    dispatch,
    handleRetry,
    hasContent,
    showLoadingOverlay,
  }
}

export default useWrappedData
